// Admin information architecture (redesign.md §6–7): sidebar modules,
// settings groups, and the quick actions ⌘K offers. One source for the
// sidebar, breadcrumbs, the command palette and the settings landing.
import type { NavIcon } from "../components/nav-icons";
import type { Key } from "../locales/en";

export type NavLink = { to: string; label: Key; icon: NavIcon; perm?: string; soon?: boolean; children?: NavLink[] };
export type NavModule = { id: string; label?: Key; icon: NavIcon; items: NavLink[] };

// Paths are relative to /store/$handle.
export const modules: NavModule[] = [
  { id: "overview", icon: "overview", items: [{ to: "", label: "nav.home", icon: "overview" }] },
  { id: "commerce", label: "nav.module.commerce", icon: "commerce", items: [
    { to: "/orders", label: "nav.orders", icon: "orders", perm: "orders.view", children: [
      { to: "/orders/manual", label: "nav.drafts", icon: "manualOrders", perm: "orders.view" },
      { to: "/orders/recovered", label: "nav.abandoned", icon: "recoveredCarts", perm: "orders.view" },
      { to: "/orders/returns", label: "nav.returns", icon: "returns", perm: "orders.view" },
    ] },
    { to: "/products", label: "nav.catalog", icon: "catalog", perm: "products.view", children: [
      { to: "/products", label: "nav.products", icon: "catalog", perm: "products.view" },
      { to: "/collections", label: "nav.collections", icon: "collections", perm: "products.view" },
      { to: "/gift_cards", label: "nav.gift_cards", icon: "giftCards", perm: "gift_cards.view" },
    ] },
    { to: "/inventory", label: "nav.inventory", icon: "inventory", perm: "inventory.view", children: [
      { to: "/inventory", label: "nav.stock_levels", icon: "inventory", perm: "inventory.view" },
      { to: "/inventory/counts", label: "nav.stock_counts", icon: "stockCounts", perm: "inventory.view" },
      { to: "/transfers", label: "nav.transfers", icon: "transfers", perm: "inventory.view" },
      { to: "/purchase-orders", label: "nav.purchase_orders", icon: "purchaseOrders", perm: "purchase_orders.manage" },
    ] },
  ] },
  { id: "people", label: "nav.module.people", icon: "people", items: [
    { to: "/customers", label: "nav.customers", icon: "customers", perm: "customers.view", children: [
      { to: "/customers/segments", label: "nav.segments", icon: "segments", perm: "customers.view" },
    ] },
    { to: "/messages", label: "nav.form_submissions", icon: "messages", perm: "online_store.content" },
  ] },
  { id: "growth", label: "nav.module.growth", icon: "growth", items: [
    { to: "/marketing", label: "nav.marketing", icon: "campaigns", perm: "marketing.view", children: [
      { to: "/marketing/campaigns", label: "nav.campaigns", icon: "emailCampaigns", perm: "marketing.view" },
      { to: "/marketing/automations", label: "nav.automations", icon: "automations", perm: "marketing.view" },
      { to: "/marketing/feeds", label: "nav.feeds", icon: "feeds", perm: "marketing.view" },
    ] },
    { to: "/discounts", label: "nav.discounts", icon: "discounts", perm: "discounts.view" },
    { to: "/reviews", label: "nav.reviews", icon: "reviews", perm: "products.view" },
  ] },
  { id: "storefront", label: "nav.module.storefront", icon: "storefront", items: [
    { to: "/storefront/themes", label: "nav.themes", icon: "themes", perm: "online_store.themes.view" },
    { to: "/storefront/features", label: "nav.features", icon: "features" },
    { to: "/storefront/pages", label: "nav.pages", icon: "pages", perm: "online_store.content" },
    { to: "/storefront/menus", label: "nav.menus", icon: "menus", perm: "online_store.content" },
    { to: "/storefront/blog", label: "nav.blog_posts", icon: "blog", perm: "online_store.content" },
    { to: "/storefront/files", label: "nav.files", icon: "files" },
    { to: "/storefront/preferences", label: "nav.preferences", icon: "preferences", perm: "online_store.content" },
    { to: "/storefront/seo", label: "nav.seo", icon: "seo", perm: "products.view" },
    { to: "/storefront/search", label: "nav.search_discovery", icon: "searchDiscovery", perm: "online_store.content" },
    { to: "/storefront/translate", label: "nav.translate", icon: "translate", perm: "online_store.content" },
    { to: "/storefront/custom-content", label: "nav.metaobjects", icon: "customContent", perm: "online_store.content" },
  ] },
  { id: "insights", label: "nav.module.insights", icon: "insights", items: [
    { to: "/analytics", label: "nav.analytics", icon: "analytics", perm: "analytics.view", children: [
      { to: "/analytics/reports", label: "nav.reports", icon: "reports", perm: "analytics.view" },
      { to: "/analytics/live", label: "nav.live_view", icon: "live", perm: "analytics.view" },
    ] },
  ] },
  // Finance (payouts, transactions, invoices) has no pages yet: hidden
  // until it does (redesign.md §6, agreed).
];

export type SettingsSection = { to: string; label: Key; hint: Key; icon: NavIcon; perm?: string };
export type SettingsGroup = { id: string; label: Key; hint: Key; icon: NavIcon; sections: SettingsSection[] };

// Paths are relative to /store/$handle/settings.
export const settingsGroups: SettingsGroup[] = [
  { id: "business", label: "settings.group.business", hint: "settings.group.business_hint", icon: "business", sections: [
    { to: "/store-profile", label: "settings.general", hint: "settings.general_hint", icon: "storeProfile", perm: "settings.general" },
    { to: "/languages", label: "settings.languages", hint: "settings.languages_hint", icon: "languages", perm: "settings.general" },
    { to: "/store-status", label: "settings.status", hint: "settings.status_hint", icon: "storeStatus", perm: "settings.general" },
    { to: "/policies", label: "policies.title", hint: "policies.settings_hint", icon: "policies", perm: "settings.general" },
  ] },
  { id: "selling", label: "settings.group.selling", hint: "settings.group.selling_hint", icon: "selling", sections: [
    { to: "/payment-methods", label: "settings.payments", hint: "settings.payments_hint", icon: "paymentMethods", perm: "settings.payments" },
    { to: "/checkout", label: "settings.checkout", hint: "settings.checkout_hint", icon: "checkout", perm: "settings.checkout" },
    { to: "/shipping", label: "settings.shipping", hint: "settings.shipping_hint", icon: "shipping", perm: "settings.shipping" },
    { to: "/taxes", label: "settings.taxes", hint: "settings.taxes_hint", icon: "taxes", perm: "settings.taxes" },
    { to: "/invoices", label: "inv.title", hint: "inv.nav_hint", icon: "invoices", perm: "settings.taxes" },
  ] },
  { id: "operations", label: "settings.group.operations", hint: "settings.group.operations_hint", icon: "operations", sections: [
    { to: "/warehouses", label: "settings.locations", hint: "settings.locations_hint", icon: "warehouses", perm: "settings.general" },
    { to: "/inventory", label: "settings.inventory", hint: "settings.inventory_hint", icon: "inventorySettings", perm: "inventory.view" },
    { to: "/notifications", label: "settings.notifications", hint: "settings.notifications_hint", icon: "notifications", perm: "settings.notifications" },
    { to: "/custom-fields", label: "settings.custom_data", hint: "settings.custom_data_hint", icon: "customFields", perm: "products.view" },
  ] },
  { id: "privacy", label: "settings.group.privacy", hint: "settings.group.privacy_hint", icon: "privacy", sections: [
    { to: "/customer-accounts", label: "settings.customer_accounts", hint: "settings.customer_accounts_hint", icon: "customerAccounts", perm: "customers.view" },
    { to: "/customer-privacy", label: "settings.customer_privacy", hint: "settings.customer_privacy_hint", icon: "customerPrivacy", perm: "customers.view" },
    { to: "/customer-events", label: "ce.title", hint: "ce.nav_hint", icon: "customerEvents", perm: "marketing.view" },
  ] },
  { id: "account", label: "settings.group.account", hint: "settings.group.account_hint", icon: "account", sections: [
    { to: "/team", label: "settings.users", hint: "settings.users_hint", icon: "team", perm: "staff.view" },
    { to: "/subscription", label: "settings.plan", hint: "settings.plan_hint", icon: "subscription", perm: "billing.view" },
    { to: "/billing", label: "settings.billing", hint: "settings.billing_hint", icon: "billing", perm: "billing.view" },
    { to: "/domains", label: "settings.domains", hint: "settings.domains_hint", icon: "domains", perm: "domains.manage" },
    { to: "/apps", label: "apps.title", hint: "apps.nav_hint", icon: "apps", perm: "apps.manage" },
    { to: "/activity", label: "settings.activity", hint: "settings.activity_hint", icon: "activity", perm: "settings.general" },
  ] },
];

// Quick actions in ⌘K: shortcuts to existing "create" screens only.
export const quickActions: { to: string; label: Key; icon: NavIcon; perm: string }[] = [
  { to: "/products/new", label: "palette.action.product", icon: "catalog", perm: "products.edit" },
  { to: "/orders/manual/new", label: "palette.action.order", icon: "orders", perm: "orders.create" },
  { to: "/customers/new", label: "palette.action.customer", icon: "customers", perm: "customers.edit" },
  { to: "/collections/new", label: "palette.action.collection", icon: "collections", perm: "products.edit" },
  { to: "/discounts/new", label: "palette.action.discount", icon: "discounts", perm: "discounts.manage" },
  { to: "/marketing/campaigns/new", label: "palette.action.campaign", icon: "emailCampaigns", perm: "marketing.manage" },
  { to: "/storefront/pages/new", label: "palette.action.page", icon: "pages", perm: "online_store.content" },
  { to: "/storefront/blog/new", label: "palette.action.post", icon: "blog", perm: "online_store.content" },
];

// activeLink finds the most specific nav entry for a store-relative path.
export function flatLinks(can: (p: string) => boolean): { link: NavLink; module: NavModule; parent?: NavLink }[] {
  const out: { link: NavLink; module: NavModule; parent?: NavLink }[] = [];
  for (const m of modules) {
    for (const it of m.items) {
      if (it.perm && !can(it.perm)) continue;
      out.push({ link: it, module: m });
      for (const c of it.children ?? []) if (!c.perm || can(c.perm)) out.push({ link: c, module: m, parent: it });
    }
  }
  return out;
}

export function matchPath(rel: string, to: string) {
  return to === "" ? rel === "" : rel === to || rel.startsWith(to + "/");
}
